import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Table, Spinner, Button, ProgressBar } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const C = {
  navy:   '#0f172a', navy2:  '#1e293b', sky:    '#0ea5e9', skyD:   '#0369a1',
  teal:   '#0d9488', tealD:  '#0f766e', green:  '#10b981', greenD: '#059669',
  amber:  '#f59e0b', violet: '#6366f1', red:    '#ef4444',
  slate:  '#f8fafc', border: '#e2e8f0', text:   '#1e293b', muted:  '#64748b', light:  '#94a3b8',
};

const fmt = (v) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(v || 0);

const Pill = ({ bg, color, children, outline }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 12, fontSize: '0.64rem', lineHeight: 1.2, fontWeight: 600, background: outline ? 'transparent' : (bg || '#f1f5f9'), color: color || C.muted, border: outline ? `1px solid ${bg || C.border}` : 'none', whiteSpace: 'nowrap' }}>{children}</span>
);

const ActionBtn = ({ icon, title, color, onClick }) => (
  <button title={title} onClick={onClick}
    style={{ background: 'transparent', border: `1px solid ${color}`, borderRadius: 5, color, width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.72rem', padding: 0, transition: 'all 0.15s' }}
    onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = '#fff'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color; }}>
    <i className={`bi ${icon}`} style={{ pointerEvents: 'none' }} />
  </button>
);


const PaginationBar = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemLabel = 'data',
  itemsPerPageOptions = [10, 25, 50, 100]
}) => {
  const [jumpVal, setJumpVal] = useState('');

  const startIdx = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIdx = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const pages = getPageNumbers();

  const handleJump = (e) => {
    e?.preventDefault?.();
    const p = parseInt(jumpVal, 10);
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      onPageChange(p);
      setJumpVal('');
    }
  };

  return (
    <div style={{
      padding: '10px 16px',
      borderTop: `1px solid ${C.border}`,
      background: '#fafbfc',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 12
    }}>
      {/* KIRI: Info Total & Items Per Page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.78rem', color: C.muted }}>
          Menampilkan <strong style={{ color: C.navy }}>{startIdx.toLocaleString('id-ID')}</strong> - <strong style={{ color: C.navy }}>{endIdx.toLocaleString('id-ID')}</strong> dari <strong style={{ color: C.navy }}>{totalItems.toLocaleString('id-ID')}</strong> {itemLabel}
        </div>
        {onItemsPerPageChange && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.76rem', color: C.muted }}>
            <span>Tampilkan</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: '3px 8px',
                borderRadius: 6,
                border: `1px solid ${C.border}`,
                background: '#fff',
                color: C.navy,
                fontSize: '0.76rem',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {itemsPerPageOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <span>baris</span>
          </div>
        )}
      </div>

      {/* KANAN: Navigasi Page & Jump Input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {totalPages > 3 && (
          <form onSubmit={handleJump} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: '0.76rem', color: C.muted }}>Ke hal:</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={jumpVal}
              onChange={(e) => setJumpVal(e.target.value)}
              placeholder={String(currentPage)}
              style={{
                width: '46px',
                padding: '3px 5px',
                borderRadius: 6,
                border: `1px solid ${C.border}`,
                textAlign: 'center',
                fontSize: '0.76rem',
                fontWeight: 600,
                outline: 'none'
              }}
            />
            <button
              type="submit"
              style={{
                background: '#fff',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                padding: '3px 8px',
                fontSize: '0.74rem',
                fontWeight: 600,
                color: C.muted,
                cursor: 'pointer'
              }}
            >
              Go
            </button>
          </form>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            title="Halaman Pertama"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            style={{
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              background: '#fff',
              color: currentPage === 1 ? '#cbd5e1' : C.muted,
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.78rem',
              transition: 'all 0.15s'
            }}
          >
            <i className="bi bi-chevron-double-left" />
          </button>

          <button
            type="button"
            title="Halaman Sebelumnya"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            style={{
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              background: '#fff',
              color: currentPage === 1 ? '#cbd5e1' : C.muted,
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.78rem',
              transition: 'all 0.15s'
            }}
          >
            <i className="bi bi-chevron-left" />
          </button>

          {pages.map((p, i) => {
            if (p === '...') {
              return (
                <span key={`dots-${i}`} style={{ padding: '0 4px', color: '#94a3b8', fontSize: '0.8rem' }}>
                  &hellip;
                </span>
              );
            }
            const isActive = p === currentPage;
            return (
              <button
                type="button"
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  minWidth: 30,
                  height: 30,
                  padding: '0 6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${isActive ? C.skyD : C.border}`,
                  borderRadius: 6,
                  background: isActive ? `linear-gradient(135deg, ${C.sky}, ${C.skyD})` : '#fff',
                  color: isActive ? '#fff' : C.text,
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  boxShadow: isActive ? '0 2px 6px rgba(14,165,233,0.3)' : 'none',
                  transition: 'all 0.15s'
                }}
              >
                {p}
              </button>
            );
          })}

          <button
            type="button"
            title="Halaman Selanjutnya"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              background: '#fff',
              color: currentPage === totalPages ? '#cbd5e1' : C.muted,
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.78rem',
              transition: 'all 0.15s'
            }}
          >
            <i className="bi bi-chevron-right" />
          </button>

          <button
            type="button"
            title="Halaman Terakhir"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              background: '#fff',
              color: currentPage === totalPages ? '#cbd5e1' : C.muted,
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.78rem',
              transition: 'all 0.15s'
            }}
          >
            <i className="bi bi-chevron-double-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

const MasterObat = () => {



  const navigate = useNavigate();
  const [time, setTime] = useState({ date: '', clock: '' });
  const [obatList, setObatList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterFormularium, setFilterFormularium] = useState('');
  const [filterGenerik, setFilterGenerik] = useState('');
  const [prinsipalList, setPrinsipalList] = useState([]);
  const [filterPrinsipal, setFilterPrinsipal] = useState('');
  const [activeTab, setActiveTab] = useState('obat');

  // State Data Generik
  const [generikList, setGenerikList] = useState([]);
  const [isLoadingGenerik, setIsLoadingGenerik] = useState(false);
  const [searchGenerik, setSearchGenerik] = useState('');
  const [pageGenerik, setPageGenerik] = useState(1);
  const [itemsPerPageGenerik, setItemsPerPageGenerik] = useState(25);
  const [pagePrinsipal, setPagePrinsipal] = useState(1);
  const [itemsPerPagePrinsipal, setItemsPerPagePrinsipal] = useState(25);
  
  // State Dialog STOK & PERSAMAAN (TableSama Gambas Replica)
  const [showStokModal, setShowStokModal] = useState(false);
  const [selectedObatStok, setSelectedObatStok] = useState(null);
  const [tableSamaList, setTableSamaList] = useState([]);
  const [kdPersamaanStok, setKdPersamaanStok] = useState('');
  const [filterTableSama, setFilterTableSama] = useState('');
  const [isLoadingTableSama, setIsLoadingTableSama] = useState(false);
  const [isCheckingStok, setIsCheckingStok] = useState(false);

  // State Dialog STANDAR OBAT DAN AKSP (Gambas Class Reimplementation)
  const [showModalLaporan, setShowModalLaporan] = useState(false);
  const [cmbDisusun, setCmbDisusun] = useState(1);
  const [txtMargin, setTxtMargin] = useState('1');
  const [txtKode, setTxtKode] = useState('');
  const [txtNama, setTxtNama] = useState('');
  const [optTidakStandar, setOptTidakStandar] = useState(false);
  const [progValue, setProgValue] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfTitle, setPdfTitle] = useState('Pratinjau Laporan Persamaan Obat');
  const [pdfFilename, setPdfFilename] = useState('Laporan_Persamaan_Obat.pdf');

  // State Modal Kartu Stok
  const [showModalKartuStok, setShowModalKartuStok] = useState(false);
  const [selectedObatKartuStok, setSelectedObatKartuStok] = useState(null);
  const [lokasiKartuStok, setLokasiKartuStok] = useState('00001'); // '00001' = Apotek, '00002' = Farmasi
  const [tglAwalKartuStok, setTglAwalKartuStok] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [tglAkhirKartuStok, setTglAkhirKartuStok] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  // Seleksi Prinsipal untuk Opsi 5 (TabelPeriksa)
  const [tabelPeriksa, setTabelPeriksa] = useState([]);
  const [txtKode2, setTxtKode2] = useState('');
  const [txtNama2, setTxtNama2] = useState('');

  // Lookup Modal for Prinsipal / Klasifikasi
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [lookupType, setLookupType] = useState('prinsipal'); // 'prinsipal' | 'klasifikasi'
  const [lookupTargetField, setLookupTargetField] = useState('field1'); // 'field1' | 'field2'
  const [lookupSearch, setLookupSearch] = useState('');
  const [lookupList, setLookupList] = useState([]);
  const [isLoadingLookup, setIsLoadingLookup] = useState(false);



  const openLookup = async (type, targetField) => {
    setLookupType(type);
    setLookupTargetField(targetField);
    setLookupSearch('');
    setShowLookupModal(true);
    setIsLoadingLookup(true);
    try {
      if (type === 'prinsipal') {
        const res = await fetch(`${getApiUrl()}/show_prinsipal`);
        const j = await res.json();
        setLookupList(j.data || j.list_prinsipal || []);
      } else {
        const res = await fetch(`${getApiUrl()}/show_klasifikasi`);
        const j = await res.json();
        setLookupList(j.list_klasifikasi || []);
      }
    } catch (e) {
      console.warn(e);
      setLookupList([]);
    } finally {
      setIsLoadingLookup(false);
    }
  };

  const selectLookupItem = (item) => {
    if (lookupType === 'prinsipal') {
      if (lookupTargetField === 'field1') {
        setTxtKode(item.kode || '');
        setTxtNama(item.nama || '');
      } else {
        setTxtKode2(item.kode || '');
        setTxtNama2(item.nama || '');
      }
    } else {
      setTxtKode(item.kd_klas || '');
      setTxtNama(item.nm_klas || '');
    }
    setShowLookupModal(false);
  };

  const handleAddSeleksiPrinsipal = () => {
    if (!txtKode2.trim()) return;
    if (tabelPeriksa.some(p => p.kd_prinsipal === txtKode2.trim())) {
      showToast('Prinsipal sudah ada dalam tabel seleksi!', 'warning');
      return;
    }
    setTabelPeriksa([...tabelPeriksa, { kd_prinsipal: txtKode2.trim(), nm_prinsipal: txtNama2.trim() }]);
    setTxtKode2('');
    setTxtNama2('');
  };

  const handleRemoveSeleksiPrinsipal = (kd) => {
    setTabelPeriksa(tabelPeriksa.filter(p => p.kd_prinsipal !== kd));
  };



  const handleOpenKartuStok = (obat) => {
    setSelectedObatKartuStok(obat);
    setLokasiKartuStok('00001');
    const d = new Date();
    setTglAwalKartuStok(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
    setTglAkhirKartuStok(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setShowModalKartuStok(true);
  };

  const handleProsesKartuStok = async () => {
    if (!selectedObatKartuStok) return;
    const kdobat = selectedObatKartuStok.kdbr || selectedObatKartuStok.kd_obat1 || selectedObatKartuStok.kd_obat || '';
    const nmobat = selectedObatKartuStok.nama || selectedObatKartuStok.nm_obat || '';
    const nmlokasi = lokasiKartuStok === '00001' ? 'Apotek' : 'Farmasi';

    if (!tglAwalKartuStok || !tglAkhirKartuStok) {
      showToast('Periode tanggal 1 dan tanggal 2 wajib diisi!', 'warning');
      return;
    }

    setShowModalKartuStok(false);
    setIsProcessing(true);
    setPdfProgress(15);
    setPdfUrl('');
    setPdfTitle(`Kartu Stok - [${kdobat}] ${nmobat} (${nmlokasi})`);
    setPdfFilename(`Kartu_Stok_${kdobat}_${lokasiKartuStok}.pdf`);
    setShowModalPdf(true);

    const progressInterval = setInterval(() => {
      setPdfProgress(prev => {
        const next = prev + 12;
        return next < 88 ? next : 88;
      });
    }, 150);

    try {
      const payload = {
        kdobat: kdobat,
        lokasi: lokasiKartuStok,
        tgl_awal: tglAwalKartuStok,
        tgl_akhir: tglAkhirKartuStok
      };

      const res = await fetch(`${getApiUrl()}/cetak_kartu_stok_pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let errMsg = `Gagal memuat PDF Kartu Stok (Status: ${res.status})`;
        try {
          const errJson = await res.json();
          if (errJson && errJson.message) errMsg = errJson.message;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const blob = await res.blob();
      const fileURL = URL.createObjectURL(blob);

      clearInterval(progressInterval);
      setPdfProgress(100);

      setTimeout(() => {
        setPdfUrl(fileURL);
        setIsProcessing(false);
        showToast('Kartu Stok PDF berhasil dibuat!', 'success');
      }, 300);

    } catch (err) {
      clearInterval(progressInterval);
      setIsProcessing(false);
      setPdfProgress(0);
      setShowModalPdf(false);
      console.error(err);
      showToast('Terjadi kesalahan saat memproses Kartu Stok PDF: ' + (err.message || ''), 'danger');
    }
  };

  const handleProsesLaporan = async () => {
    if (Number(cmbDisusun) === 3 && !txtKode.trim()) {
      showToast('Klasifikasi harus dipilih!', 'warning');
      return;
    }
    if (Number(cmbDisusun) === 5 && tabelPeriksa.length === 0 && !txtKode.trim()) {
      showToast('Pilih minimal satu Prinsipal untuk seleksi!', 'warning');
      return;
    }

    setIsProcessing(true);
    setProgValue(10);
    setPdfProgress(15);
    setPdfUrl('');
    setPdfTitle('Pratinjau Laporan Persamaan Obat');
    setPdfFilename(`Laporan_Persamaan_Obat_${cmbDisusun}.pdf`);
    setShowModalPdf(true);

    const progressInterval = setInterval(() => {
      setProgValue(prev => {
        const next = prev + 15;
        return next < 90 ? next : 90;
      });
      setPdfProgress(prev => {
        const next = prev + 15;
        return next < 90 ? next : 90;
      });
    }, 120);

    try {
      const selPrinsipal = tabelPeriksa.map(p => p.kd_prinsipal).join(',');
      const params = new URLSearchParams({
        disusun: String(cmbDisusun),
        kode: txtKode || '',
        margin: String(parseFloat(txtMargin) || 1.0),
        opt_tidak_standar: optTidakStandar ? 'true' : 'false',
        selected_prinsipal: selPrinsipal
      });

      const endpointUrl = `${getApiUrl()}/cetak_laporan_persamaan_pdf?${params.toString()}`;
      const res = await fetch(endpointUrl);
      if (!res.ok) {
        throw new Error(`Gagal memuat PDF laporan (Status: ${res.status})`);
      }

      const blob = await res.blob();
      const fileURL = URL.createObjectURL(blob);

      clearInterval(progressInterval);
      setProgValue(100);
      setPdfProgress(100);

      setTimeout(() => {
        setPdfUrl(fileURL);
        setIsProcessing(false);
        showToast('Laporan PDF berhasil dibuat!', 'success');
      }, 300);

    } catch (err) {
      clearInterval(progressInterval);
      setIsProcessing(false);
      setProgValue(0);
      setPdfProgress(0);
      setShowModalPdf(false);
      console.error(err);
      showToast('Terjadi kesalahan saat memproses laporan PDF: ' + (err.message || ''), 'danger');
    }
  };

    // State Modal Persamaan Obat
  const [showPersamaanModal, setShowPersamaanModal] = useState(false);
  const [selectedObatUntukPersamaan, setSelectedObatUntukPersamaan] = useState(null);
  const [persamaanList, setPersamaanList] = useState([]);
  const [isLoadingPersamaan, setIsLoadingPersamaan] = useState(false);
  const [kdPersamaan, setKdPersamaan] = useState('');

  const handleOpenPersamaan = async (obat) => {
    setSelectedObatUntukPersamaan(obat);
    setShowPersamaanModal(true);
    setIsLoadingPersamaan(true);
    setPersamaanList([]);
    setKdPersamaan('');
    try {
      const response = await fetch(`${getApiUrl()}/show_persamaan?kdobat=${encodeURIComponent(obat.kd_obat1 || obat.kdbr || obat.kode)}`);
      const json = await response.json();
      if (json) {
        if (json.data) setPersamaanList(json.data);
        if (json.kd_persamaan) setKdPersamaan(json.kd_persamaan);
      }
    } catch (err) {
      console.warn("Gagal fetch data persamaan obat:", err);
    } finally {
      setIsLoadingPersamaan(false);
    }
  };

  // Handlers Dialog STOK & PERSAMAAN (TableSama)
  const fetchStokPersamaan = async (kdobat) => {
    setIsLoadingTableSama(true);
    try {
      const res = await fetch(`${getApiUrl()}/show_stok_persamaan?kdobat=${encodeURIComponent(kdobat)}`);
      const json = await res.json();
      if (json && json.data) {
        setTableSamaList(json.data);
        setKdPersamaanStok(json.kd_persamaan || kdobat);
      }
    } catch (err) {
      console.warn("Gagal fetch data stok persamaan:", err);
      showToast("Gagal memuat data stok persamaan", "warning");
    } finally {
      setIsLoadingTableSama(false);
    }
  };

  const handleOpenStok = (obat) => {
    setSelectedObatStok(obat);
    setFilterTableSama('');
    setShowStokModal(true);
    const kd = obat.kd_obat1 || obat.kdbr || obat.kode;
    fetchStokPersamaan(kd);
  };

  const handleCekStokRealtime = async () => {
    if (!kdPersamaanStok && !selectedObatStok) return;
    setIsCheckingStok(true);
    try {
      const targetKd = kdPersamaanStok || (selectedObatStok.kd_obat1 || selectedObatStok.kdbr || selectedObatStok.kode);
      const res = await fetch(`${getApiUrl()}/cek_stok_persamaan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kd_persamaan: targetKd })
      });
      const json = await res.json();
      if (json && json.data) {
        setTableSamaList(json.data);
        showToast("Stok real-time RJ & RI berhasil diperbarui!", "success");
      }
    } catch (err) {
      console.error(err);
      showToast("Gagal cek stok real-time", "danger");
    } finally {
      setIsCheckingStok(false);
    }
  };

  const filteredTableSama = tableSamaList.filter(item => {
    if (!filterTableSama) return true;
    const q = filterTableSama.toLowerCase();
    return (item.kd_obat || '').toLowerCase().includes(q) ||
           (item.nm_obat || '').toLowerCase().includes(q) ||
           (item.satuan || '').toLowerCase().includes(q);
  });

  // State Modal Isi Generik
  const [showIsiGenerikModal, setShowIsiGenerikModal] = useState(false);
  const [selectedObatUntukGenerik, setSelectedObatUntukGenerik] = useState(null);
  const [isiGenerikList, setIsiGenerikList] = useState([]);
  const [isLoadingIsiGenerik, setIsLoadingIsiGenerik] = useState(false);

  const fetchGenerik = async () => {
    setIsLoadingGenerik(true);
    try {
      const response = await fetch(`${getApiUrl()}/show_generik`);
      const json = await response.json();
      if (json && json.list_generik) {
        setGenerikList(json.list_generik);
      }
    } catch (err) {
      console.warn("Gagal fetch data generik:", err);
    } finally {
      setIsLoadingGenerik(false);
    }
  };

  const handleOpenIsiGenerik = async (obat) => {
    setSelectedObatUntukGenerik(obat);
    setShowIsiGenerikModal(true);
    setIsLoadingIsiGenerik(true);
    setIsiGenerikList([]);
    try {
      const response = await fetch(`${getApiUrl()}/show_isigenerik?kdobat=${obat.kd_obat1 || obat.kode}`);
      const json = await response.json();
      if (json && json.list_isi) {
        setIsiGenerikList(json.list_isi);
      }
    } catch (err) {
      console.warn("Gagal fetch data isi generik:", err);
    } finally {
      setIsLoadingIsiGenerik(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'generik' && generikList.length === 0) {
      fetchGenerik();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, generikList.length]);

  const filteredGenerik = generikList.filter(item => 
    (item.nama || '').toLowerCase().includes(searchGenerik.toLowerCase()) ||
    (item.kode || '').toLowerCase().includes(searchGenerik.toLowerCase())
  );
  const [dataPrinsipal, setDataPrinsipal] = useState([]);
  const [isLoadingPrinsipal, setIsLoadingPrinsipal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [confirmDel, setConfirmDel] = useState(null);
  const [showModalForm, setShowModalForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showModalDetail, setShowModalDetail] = useState(false);
  const [selectedObat, setSelectedObat] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const initialForm = { kd_obat1: '', kd_obat2: '', nm_obat: '', satuan: '', harga: 0, stoka: 0, stokf: 0, m1: 'Tidak', generik: '', prinsipal: '', khusus: 'X', jamsostek: '' };
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    const tick = () => { const n = new Date(); setTime({ date: n.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }), clock: n.toLocaleTimeString('id-ID') + ' WIB' }); };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, []);

  useEffect(() => { const t = setTimeout(() => { setDebouncedSearch(searchQuery); setCurrentPage(1); }, 400); return () => clearTimeout(t); }, [searchQuery]);

  const showToast = (message, variant = 'success') => { setToast({ show: true, message, variant }); setTimeout(() => setToast({ show: false, message: '', variant: 'success' }), 4000); };

  useEffect(() => {
    const load = async () => { try { const token = localStorage.getItem('token'); const res = await fetch(`${getApiUrl()}/show_prinsipal_obat?token=${encodeURIComponent(token || '')}`); if (res.ok) { const d = await res.json(); if (d.list_prinsipal) setPrinsipalList(d.list_prinsipal); } } catch (e) { console.warn('Gagal memuat prinsipal:', e); } };
    load();
  }, []);

  const fetchDataPrinsipal = useCallback(async () => {
    setIsLoadingPrinsipal(true); const token = localStorage.getItem('token');
    try { const res = await fetch(`${getApiUrl()}/show_prinsipal?token=${encodeURIComponent(token || '')}`); const j = await res.json(); if (j.data) setDataPrinsipal(j.data); } catch (e) { console.error(e); }
    setIsLoadingPrinsipal(false);
  }, []);

  useEffect(() => { if (activeTab === 'prinsipal' && dataPrinsipal.length === 0) fetchDataPrinsipal(); }, [activeTab, dataPrinsipal.length, fetchDataPrinsipal]);

  const fetchObat = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl()}/show_obat?token=${encodeURIComponent(token || '')}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ search: debouncedSearch, page: currentPage, limit: itemsPerPage, formularium: filterFormularium, generik: filterGenerik, prinsipal: filterPrinsipal }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setObatList(data.list_obat || data.data || []); setTotalItems(data.total || 0); setTotalPages(data.total_pages || Math.ceil((data.total || 0) / itemsPerPage) || 1);
    } catch (err) { showToast('Gagal memuat data: ' + err.message, 'danger'); setObatList([]); }
    finally { setIsLoading(false); }
  }, [debouncedSearch, currentPage, itemsPerPage, filterFormularium, filterGenerik, filterPrinsipal]);

  useEffect(() => { fetchObat(); }, [fetchObat]);

  const handleOpenCreate = () => { setIsEditing(false); setFormData(initialForm); setShowModalForm(true); };
  const handleOpenEdit = (item) => {
    setIsEditing(true);
    setFormData({ kd_obat1: item.kd_obat1 || '', kd_obat2: item.kd_obat2 || item.kd_obat1 || '', nm_obat: item.nm_obat || '', satuan: item.satuan || '', harga: item.harga || 0, stoka: item.stoka || 0, stokf: item.stokf || 0, m1: item.m1 || 'Tidak', generik: item.generik || '', prinsipal: item.prinsipal || '', khusus: item.khusus || 'X', jamsostek: item.jamsostek || '' });
    setShowModalForm(true);
  };
  const handleOpenDetail = (item) => { setSelectedObat(item); setShowModalDetail(true); };

  const handleSaveForm = async (e) => {
    e.preventDefault();
    if (!formData.kd_obat1 || !formData.nm_obat) { showToast('Kode Obat dan Nama Obat wajib diisi!', 'warning'); return; }
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token'); const endpoint = isEditing ? '/update_obat' : '/simpan_obat';
      const res = await fetch(`${getApiUrl()}${endpoint}?token=${encodeURIComponent(token || '')}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      const rd = await res.json(); if (!res.ok || rd.status === 'error') throw new Error(rd.message || 'Gagal menyimpan');
      showToast(rd.message || 'Data berhasil disimpan!'); setShowModalForm(false); fetchObat();
    } catch (err) { showToast('Error: ' + err.message, 'danger'); } finally { setIsSaving(false); }
  };

  const handleDelete = async (item) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiUrl()}/hapus_obat?token=${encodeURIComponent(token || '')}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kd_obat1: item.kd_obat1, kd_obat2: item.kd_obat2 }) });
      const rd = await res.json(); if (!res.ok || rd.status === 'error') throw new Error(rd.message || 'Gagal menghapus');
      showToast('Data obat berhasil dihapus!'); fetchObat();
    } catch (err) { showToast('Error: ' + err.message, 'danger'); }
    setConfirmDel(null);
  };


  const iSt = { width: '100%', boxSizing: 'border-box', background: C.slate, border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 13px', fontSize: '0.84rem', color: C.text, outline: 'none' };
  const td0 = { padding: '2.5px 6px', borderBottom: `1px solid ${C.slate}`, verticalAlign: 'middle', lineHeight: 1.25 };
  const selSt = { background: C.slate, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: '0.8rem', padding: '5px 10px', outline: 'none', cursor: 'pointer' };

  return (
    <div style={{ background: '#f1f5f9', minHeight: '100vh', fontFamily: "'Inter','Segoe UI',sans-serif", paddingBottom: 40 }}>

      {/* TOPBAR */}
      <div style={{ background: `linear-gradient(135deg,${C.navy},${C.navy2})`, padding: '0 24px', height: 50, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,0.18)', position: 'sticky', top: 0, zIndex: 1050 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => navigate('/home')} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: 8, color: '#e2e8f0', padding: '5px 13px', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            <i className="bi bi-arrow-left-short" style={{ fontSize: '1rem' }} /> Dashboard
          </button>
          <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.1)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#0d9488,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(13,148,136,0.45)' }}>
              <i className="bi bi-capsule-pill" style={{ color: '#fff', fontSize: '1rem' }} />
            </div>
            <div>
              <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                Master Data Obat
                <span style={{ background: 'rgba(13,148,136,0.25)', border: '1px solid rgba(13,148,136,0.45)', color: '#2dd4bf', borderRadius: 20, padding: '1px 9px', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.6px' }}>APOTEK</span>
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginTop: 1 }}>Katalog Obat &middot; Formularium &middot; Stok</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="d-none d-md-block" style={{ textAlign: 'right' }}>
            <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.78rem' }}><i className="bi bi-clock me-1" />{time.clock}</div>
            <div style={{ color: '#64748b', fontSize: '0.68rem' }}>{time.date}</div>
          </div>
          <button onClick={() => navigate('/SuratPesananApotek')} style={{ background: 'linear-gradient(135deg,#0284c7,#0369a1)', border: 'none', borderRadius: 8, color: '#fff', padding: '7px 15px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 3px 10px rgba(2,132,199,0.35)' }}>
            <i className="bi bi-cart-check-fill" /> Surat Pesanan (SP)
          </button>
          <button onClick={() => setShowModalLaporan(true)} style={{ background: 'linear-gradient(135deg,#0d9488,#0f766e)', border: 'none', borderRadius: 8, color: '#fff', padding: '7px 15px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 3px 10px rgba(13,148,136,0.35)' }}>
            <i className="bi bi-printer-fill" /> Laporan Persamaan
          </button>
          <button onClick={handleOpenCreate} style={{ background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', borderRadius: 8, color: '#fff', padding: '7px 15px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, boxShadow: '0 3px 10px rgba(16,185,129,0.4)' }}>
            <i className="bi bi-plus-lg" /> Tambah Obat
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 24px 0' }}>

        {/* TOAST */}
        {toast.show && (
          <div style={{ background: toast.variant === 'danger' ? '#fef2f2' : toast.variant === 'warning' ? '#fffbeb' : '#f0fdf4', border: `1px solid ${toast.variant === 'danger' ? '#fecaca' : toast.variant === 'warning' ? '#fde68a' : '#bbf7d0'}`, borderLeft: `4px solid ${toast.variant === 'danger' ? C.red : toast.variant === 'warning' ? C.amber : C.green}`, borderRadius: 10, padding: '8px 14px', marginBottom: 16, color: toast.variant === 'danger' ? '#991b1b' : toast.variant === 'warning' ? '#92400e' : '#166534', fontSize: '0.83rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><i className={`bi ${toast.variant === 'danger' ? 'bi-exclamation-triangle-fill' : toast.variant === 'warning' ? 'bi-exclamation-circle-fill' : 'bi-check-circle-fill'} me-2`} />{toast.message}</span>
            <button onClick={() => setToast({ show: false, message: '', variant: 'success' })} style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: 0 }}>x</button>
          </div>
        )}

        {/* KPI */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total Katalog', value: `${totalItems.toLocaleString('id-ID')} item`, icon: 'bi-box-seam-fill', grad: `linear-gradient(135deg,${C.sky},${C.skyD})` },
            { label: 'Formularium', value: 'M1 Aktif', icon: 'bi-check2-all', grad: `linear-gradient(135deg,${C.teal},${C.tealD})` },
            { label: 'Kategori', value: 'Gen & Non-Gen', icon: 'bi-tags-fill', grad: `linear-gradient(135deg,${C.violet},#4f46e5)` },
            { label: 'Prinsipal', value: `${prinsipalList.length} vendor`, icon: 'bi-buildings-fill', grad: `linear-gradient(135deg,${C.amber},#d97706)` },
          ].map((k, i) => (
            <div key={i} style={{ background: k.grad, borderRadius: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 4 }}>{k.label}</div>
                <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700 }}>{k.value}</div>
              </div>
              <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: '#fff' }}><i className={`bi ${k.icon}`} /></div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, display: 'inline-flex', padding: 4, gap: 4, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {[
            { key: 'obat', icon: 'bi-capsule', label: 'Master Obat', grad: `linear-gradient(135deg,${C.sky},${C.skyD})` },
            { key: 'prinsipal', icon: 'bi-buildings', label: 'Data Prinsipal', grad: `linear-gradient(135deg,${C.green},${C.greenD})` },
{ key: 'generik', icon: 'bi-capsule-pill', label: 'Data Generik', grad: `linear-gradient(135deg,${C.amber},#d97706)` },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: '5px 14px', borderRadius: 8, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s', border: 'none', background: activeTab === t.key ? t.grad : 'transparent', color: activeTab === t.key ? '#fff' : C.muted, boxShadow: activeTab === t.key ? '0 2px 8px rgba(0,0,0,0.15)' : 'none' }}>
              <i className={`bi ${t.icon} me-2`} />{t.label}
            </button>
          ))}
        </div>

        {/* TAB OBAT */}
        {activeTab === 'obat' && (
          <>
            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, padding: '8px 12px', marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ flex: '1 1 260px', display: 'flex', alignItems: 'center', background: C.slate, border: `1px solid ${C.border}`, borderRadius: 8, padding: '0 11px', gap: 7 }}>
                <i className="bi bi-search" style={{ color: C.light, fontSize: '0.82rem' }} />
                <input type="text" placeholder="Cari nama obat, kode, prinsipal..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ background: 'transparent', border: 'none', outline: 'none', color: C.text, fontSize: '0.82rem', width: '100%', padding: '5px 0' }} />
                {searchQuery && <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', color: C.light, cursor: 'pointer', padding: 0, fontSize: '1rem' }}>x</button>}
              </div>
              <select value={filterFormularium} onChange={e => { setFilterFormularium(e.target.value); setCurrentPage(1); }} style={{ ...selSt, minWidth: 155 }}>
                <option value="">Semua Formularium</option><option value="YA">Formularium (YA)</option><option value="TIDAK">Non-Formularium</option>
              </select>
              <select value={filterGenerik} onChange={e => { setFilterGenerik(e.target.value); setCurrentPage(1); }} style={{ ...selSt, minWidth: 145 }}>
                <option value="">Semua Golongan</option><option value="YA">Generik Saja</option><option value="TIDAK">Non-Generik</option>
              </select>
              <select value={filterPrinsipal} onChange={e => { setFilterPrinsipal(e.target.value); setCurrentPage(1); }} style={{ ...selSt, minWidth: 165 }}>
                <option value="">Semua Prinsipal</option>
                {prinsipalList.slice(0, 60).map((p, i) => <option key={i} value={p}>{p}</option>)}
              </select>
              <select value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ ...selSt, minWidth: 95 }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / hal</option>)}
              </select>
              <button onClick={fetchObat} style={{ background: C.slate, border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, padding: '5px 12px', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <i className={`bi bi-arrow-clockwise ${isLoading ? 'spin' : ''}`} /> Refresh
              </button>
            </div>

            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ background: C.slate, borderBottom: `1px solid ${C.border}`, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: C.text, fontSize: '0.85rem' }}>
                  <i className="bi bi-table" style={{ color: C.sky }} />
                  Daftar Master Data Obat
                  <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 600 }}>{obatList.length} dari {totalItems.toLocaleString('id-ID')} data</span>
                </div>
                {isLoading && <div style={{ fontSize: '0.78rem', color: C.teal, display: 'flex', alignItems: 'center', gap: 6 }}><span className="spinner-border spinner-border-sm" role="status" /> Memuat...</div>}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {[['No',42,'c'],['Kode',115,'l'],['Nama Obat',null,'l'],['Satuan',80,'c'],['Prinsipal',160,'l'],['Harga',120,'r'],['Stok A/F',110,'c'],['Formul.',80,'c'],['Khusus',80,'c'],['Aksi',135,'c']].map(([label,w,align],i) => (
                        <th key={i} style={{ padding: '4px 6px', fontWeight: 700, color: C.muted, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, textAlign: align==='c'?'center':align==='r'?'right':'left', whiteSpace: 'nowrap', width: w }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && obatList.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding: '50px 20px', textAlign: 'center', color: C.light }}><span className="spinner-border spinner-border-sm me-2" role="status" />Memuat data...</td></tr>
                    ) : obatList.length === 0 ? (
                      <tr><td colSpan={10} style={{ padding: '60px 20px', textAlign: 'center', color: C.light }}>
                        <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />Tidak ada data yang sesuai.
                      </td></tr>
                    ) : obatList.map((item, idx) => {
                      const no = (currentPage - 1) * itemsPerPage + idx + 1;
                      const isF = (item.m1 || '').toUpperCase() === 'YA';
                      const isG = item.generik && item.generik.trim() !== '' && item.generik.toUpperCase() !== 'TIDAK';
                      const isHov = hoveredRow === idx;
                      return (
                        <tr key={`${item.kd_obat1}-${idx}`} onMouseEnter={() => setHoveredRow(idx)} onMouseLeave={() => setHoveredRow(null)} style={{ background: isHov ? '#f0f9ff' : '#fff', transition: 'background 0.12s' }}>
                          <td style={{ ...td0, textAlign: 'center', color: C.light, fontWeight: 500 }}>{no}</td>
                          <td style={td0}>
                            <div style={{ fontWeight: 700, fontFamily: 'monospace', color: C.skyD, fontSize: '0.78rem' }}>{item.kd_obat1}</div>
                            {item.kd_obat2 && item.kd_obat2 !== item.kd_obat1 && <div style={{ color: C.light, fontSize: '0.68rem', fontFamily: 'monospace' }}>{item.kd_obat2}</div>}
                          </td>
                          <td style={td0}>
                            <div style={{ fontWeight: 600, color: C.navy, marginBottom: 1, lineHeight: 1.2 }}>{item.nm_obat}</div>
                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              {isG ? <Pill bg="rgba(16,185,129,0.12)" color={C.greenD}>Generik</Pill> : <Pill bg="rgba(148,163,184,0.1)" color={C.light} outline>Non-Generik</Pill>}
                              {item.jamsostek && <Pill bg="rgba(245,158,11,0.13)" color="#b45309">BPJS</Pill>}
                            </div>
                          </td>
                          <td style={{ ...td0, textAlign: 'center' }}><Pill bg="#f1f5f9" color={C.muted}>{item.satuan || '-'}</Pill></td>
                          <td style={{ ...td0, color: C.muted, fontSize: '0.77rem' }}>{item.prinsipal || <span style={{ color: C.border }}>-</span>}</td>
                          <td style={{ ...td0, textAlign: 'right', fontWeight: 700, color: C.navy, fontSize: '0.78rem' }}>{fmt(item.harga)}</td>
                          <td style={{ ...td0, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                              <Pill bg="rgba(2,132,199,0.1)" color={C.skyD}>A:{item.stoka||0}</Pill>
                              <Pill bg="rgba(16,185,129,0.1)" color={C.greenD}>F:{item.stokf||0}</Pill>
                            </div>
                          </td>
                          <td style={{ ...td0, textAlign: 'center' }}>
                            {isF ? <Pill bg="rgba(16,185,129,0.13)" color={C.greenD}>YA</Pill> : <Pill outline bg={C.border} color={C.light}>Tidak</Pill>}
                          </td>
                          <td style={{ ...td0, textAlign: 'center' }}>
                            {item.khusus === 'Y' ? <Pill bg="rgba(239,68,68,0.1)" color={C.red}>Khusus</Pill> : <span style={{ color: C.light, fontSize: '0.75rem' }}>Reguler</span>}
                          </td>
                          <td style={{ ...td0, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                              <ActionBtn icon="bi-eye" title="Detail" color={C.sky} onClick={() => handleOpenDetail(item)} />
                              <ActionBtn icon="bi-box-seam" title="Cek Stok & Persamaan (TableSama)" color="#059669" onClick={() => handleOpenStok(item)} />
                              <ActionBtn icon="bi-card-checklist" title="Kartu Stok (Pergerakan Barang)" color="#0284c7" onClick={() => handleOpenKartuStok(item)} />
                              <ActionBtn icon="bi-capsule" title="Lihat Isi Generik" color={C.amber} onClick={() => handleOpenIsiGenerik(item)} />
                              <ActionBtn icon="bi-shuffle" title="Lihat Persamaan Obat" color={C.teal} onClick={() => handleOpenPersamaan(item)} />
                              <ActionBtn icon="bi-pencil" title="Edit" color={C.violet} onClick={() => handleOpenEdit(item)} />
                              {/* Tombol Hapus disembunyikan sesuai permintaan */}
                              {/* <ActionBtn icon="bi-trash3" title="Hapus" color={C.red} onClick={() => setConfirmDel(item)} /> */}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(p) => setCurrentPage(p)}
                onItemsPerPageChange={(limit) => {
                  setItemsPerPage(limit);
                  setCurrentPage(1);
                }}
                itemLabel="item obat"
              />
            </div>
          </>
        )}

        {/* TAB PRINSIPAL */}
        {activeTab === 'prinsipal' && (
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <div style={{ background: C.slate, borderBottom: `1px solid ${C.border}`, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: C.text, fontSize: '0.85rem' }}>
                <i className="bi bi-buildings" style={{ color: C.green }} />
                Daftar Master Prinsipal
                <span style={{ background: '#d1fae5', color: C.greenD, borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 600 }}>{dataPrinsipal.length} vendor</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isLoadingPrinsipal && <span style={{ fontSize: '0.78rem', color: C.green, display: 'flex', alignItems: 'center', gap: 5 }}><span className="spinner-border spinner-border-sm" role="status" /> Memuat...</span>}
                <button onClick={() => { setDataPrinsipal([]); fetchDataPrinsipal(); }} style={{ background: C.slate, border: `1px solid ${C.border}`, borderRadius: 7, color: C.muted, padding: '5px 11px', cursor: 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className={`bi bi-arrow-clockwise ${isLoadingPrinsipal ? 'spin' : ''}`} /> Refresh
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {[['NO',50],['KODE',100],['NAMA PRINSIPAL',220],['ALAMAT',null],['KONTAK',150],['TELP / HP',150]].map(([h,w],i) => (
                      <th key={i} style={{ padding: '4px 6px', fontWeight: 700, color: C.muted, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, textAlign: i===0?'center':'left', width: w, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoadingPrinsipal ? (
                    <tr><td colSpan={6} style={{ padding: '50px 20px', textAlign: 'center', color: C.light }}><span className="spinner-border spinner-border-sm me-2" />Memuat...</td></tr>
                  ) : dataPrinsipal.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: '60px 20px', textAlign: 'center', color: C.light }}>
                      <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />Tidak ada data prinsipal.
                    </td></tr>
                  ) : dataPrinsipal.slice((pagePrinsipal - 1) * itemsPerPagePrinsipal, pagePrinsipal * itemsPerPagePrinsipal).map((p, idx) => (
                    <tr key={idx} onMouseEnter={e => e.currentTarget.style.background='#f0fdf4'} onMouseLeave={e => e.currentTarget.style.background='#fff'} style={{ background: '#fff', transition: 'background 0.12s' }}>
                      <td style={{ ...td0, textAlign: 'center', fontWeight: 600, color: C.light }}>{(pagePrinsipal - 1) * itemsPerPagePrinsipal + idx + 1}</td>
                      <td style={td0}><span style={{ fontFamily: 'monospace', fontWeight: 700, color: C.green, fontSize: '0.8rem' }}>{p.kode||'-'}</span></td>
                      <td style={{ ...td0, fontWeight: 700, color: C.navy }}>{p.nama||'-'}</td>
                      <td style={{ ...td0, color: C.muted, fontSize: '0.76rem', maxWidth: 280 }}>{p.alamat||'-'}</td>
                      <td style={{ ...td0, color: C.text }}>{p.kontak||'-'}</td>
                      <td style={td0}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.76rem' }}>
                          <span><i className="bi bi-telephone" style={{ color: C.light, marginRight: 5 }} />{p.telp||'-'}</span>
                          {p.hp && <span><i className="bi bi-phone" style={{ color: C.light, marginRight: 5 }} />{p.hp}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {dataPrinsipal.length > 0 && (
              <PaginationBar
                currentPage={pagePrinsipal}
                totalPages={Math.max(1, Math.ceil(dataPrinsipal.length / itemsPerPagePrinsipal))}
                totalItems={dataPrinsipal.length}
                itemsPerPage={itemsPerPagePrinsipal}
                onPageChange={(p) => setPagePrinsipal(p)}
                onItemsPerPageChange={(limit) => {
                  setItemsPerPagePrinsipal(limit);
                  setPagePrinsipal(1);
                }}
                itemLabel="vendor prinsipal"
              />
            )}
          </div>
        )}

        {/* TAB DATA GENERIK */}
        {activeTab === 'generik' && (
          <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <div style={{ background: C.slate, borderBottom: `1px solid ${C.border}`, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: C.text, fontSize: '0.85rem' }}>
                <i className="bi bi-capsule-pill" style={{ color: C.amber }} />
                Daftar Master Data Generik
                <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 600 }}>{filteredGenerik.length} generik</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 7, border: `1px solid ${C.border}`, padding: '2px 8px' }}>
                  <i className="bi bi-search text-muted me-2" style={{ fontSize: '0.75rem' }} />
                  <input
                    type="text"
                    placeholder="Cari kode atau nama generik..."
                    value={searchGenerik}
                    onChange={(e) => { setSearchGenerik(e.target.value); setPageGenerik(1); }}
                    style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.78rem', width: 220 }}
                  />
                  {searchGenerik && (
                    <button onClick={() => setSearchGenerik('')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.muted, padding: 0 }}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
                {isLoadingGenerik && <span style={{ fontSize: '0.78rem', color: C.amber, display: 'flex', alignItems: 'center', gap: 5 }}><span className="spinner-border spinner-border-sm" role="status" /> Memuat...</span>}
                <button onClick={() => { setGenerikList([]); fetchGenerik(); }} style={{ background: C.slate, border: `1px solid ${C.border}`, borderRadius: 7, color: C.muted, padding: '5px 11px', cursor: 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <i className={`bi bi-arrow-clockwise ${isLoadingGenerik ? 'spin' : ''}`} /> Refresh
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {[['NO',60],['KODE GENERIK',140],['NAMA KANDUNGAN GENERIK',null]].map(([h,w],i) => (
                      <th key={i} style={{ padding: '4px 6px', fontWeight: 700, color: C.muted, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, textAlign: i===0?'center':'left', width: w, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoadingGenerik ? (
                    <tr><td colSpan={3} style={{ padding: '50px 20px', textAlign: 'center', color: C.light }}><span className="spinner-border spinner-border-sm me-2" />Memuat...</td></tr>
                  ) : filteredGenerik.length === 0 ? (
                    <tr><td colSpan={3} style={{ padding: '60px 20px', textAlign: 'center', color: C.light }}>
                      <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />Tidak ada data generik.
                    </td></tr>
                  ) : filteredGenerik.slice((pageGenerik - 1) * itemsPerPageGenerik, pageGenerik * itemsPerPageGenerik).map((g, idx) => (
                    <tr key={idx} onMouseEnter={e => e.currentTarget.style.background='#fffbeb'} onMouseLeave={e => e.currentTarget.style.background='#fff'} style={{ background: '#fff', transition: 'background 0.12s' }}>
                      <td style={{ ...td0, textAlign: 'center', fontWeight: 600, color: C.light }}>{(pageGenerik - 1) * itemsPerPageGenerik + idx + 1}</td>
                      <td style={td0}><span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#d97706', fontSize: '0.8rem' }}>{g.kode||'-'}</span></td>
                      <td style={{ ...td0, fontWeight: 700, color: C.navy }}>{g.nama||'-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredGenerik.length > 0 && (
              <PaginationBar
                currentPage={pageGenerik}
                totalPages={Math.max(1, Math.ceil(filteredGenerik.length / itemsPerPageGenerik))}
                totalItems={filteredGenerik.length}
                itemsPerPage={itemsPerPageGenerik}
                onPageChange={(p) => setPageGenerik(p)}
                onItemsPerPageChange={(limit) => {
                  setItemsPerPageGenerik(limit);
                  setPageGenerik(1);
                }}
                itemLabel="generik"
              />
            )}
          </div>
        )}

      </div>

      {/* MODAL TAMBAH/EDIT */}
      {showModalForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={e => { if (e.target === e.currentTarget && !isSaving) setShowModalForm(false); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 30px 70px rgba(0,0,0,0.35)', animation: 'modalPop 0.22s cubic-bezier(.34,1.56,.64,1)' }}>
            <div style={{ background: `linear-gradient(135deg,${C.navy},${C.navy2})`, padding: '16px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                <i className={`bi ${isEditing ? 'bi-pencil-square' : 'bi-plus-circle'}`} style={{ color: isEditing ? '#fbbf24' : '#34d399' }} />
                {isEditing ? 'Ubah Master Data Obat' : 'Tambah Obat Baru ke Katalog'}
              </div>
              {!isSaving && <button onClick={() => setShowModalForm(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 7, color: '#94a3b8', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.1rem' }}>x</button>}
            </div>
            <form onSubmit={handleSaveForm}>
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {[{label:'Kode Obat 1',key:'kd_obat1',req:true,dis:isEditing,ph:'Contoh: 00000070'},{label:'Kode Obat 2',key:'kd_obat2',req:false,dis:isEditing,ph:'Opsional'}].map(f => (
                    <div key={f.key}>
                      <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>{f.label} {f.req && <span style={{ color: C.red }}>*</span>}</label>
                      <input disabled={f.dis} value={formData[f.key]} placeholder={f.ph} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} style={{ ...iSt, background: f.dis ? '#f1f5f9' : C.slate, color: f.dis ? C.light : C.text, cursor: f.dis ? 'not-allowed' : 'text' }} />
                    </div>
                  ))}
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>Nama Barang / Obat <span style={{ color: C.red }}>*</span></label>
                  <input value={formData.nm_obat} placeholder="Contoh: Amoxicillin 500 mg Kapsul" onChange={e => setFormData({ ...formData, nm_obat: e.target.value })} required style={iSt} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>Satuan</label>
                    <input value={formData.satuan} placeholder="Tablet, Fls..." onChange={e => setFormData({ ...formData, satuan: e.target.value })} style={iSt} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>Harga Satuan (Rp)</label>
                    <input type="number" min={0} value={formData.harga} onChange={e => setFormData({ ...formData, harga: Number(e.target.value) })} style={iSt} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>Formularium (M1)</label>
                    <select value={formData.m1} onChange={e => setFormData({ ...formData, m1: e.target.value })} style={{ ...iSt, cursor: 'pointer' }}>
                      <option value="Ya">Ya (Formularium)</option><option value="Tidak">Tidak</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {[['Stok Apotek','stoka'],['Stok Farmasi','stokf']].map(([label,key]) => (
                    <div key={key}>
                      <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>{label}</label>
                      <input type="number" min={0} value={formData[key]} onChange={e => setFormData({ ...formData, [key]: Number(e.target.value) })} style={iSt} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {[['Prinsipal / Pabrikan','prinsipal','Nama produsen...'],['Nama Generik / Zat Aktif','generik','Zat aktif...']].map(([label,key,ph]) => (
                    <div key={key}>
                      <label style={{ display: 'block', fontWeight: 600, color: C.muted, fontSize: '0.76rem', marginBottom: 5 }}>{label}</label>
                      <input value={formData[key]||''} placeholder={ph} onChange={e => setFormData({ ...formData, [key]: e.target.value })} style={iSt} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10, background: C.slate, borderRadius: '0 0 16px 16px' }}>
                <button type="button" disabled={isSaving} onClick={() => setShowModalForm(false)} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, color: C.muted, padding: '8px 18px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>Batal</button>
                <button type="submit" disabled={isSaving} style={{ background: `linear-gradient(135deg,${C.sky},${C.skyD})`, border: 'none', borderRadius: 9, color: '#fff', padding: '8px 22px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 4px 12px rgba(2,132,199,0.3)' }}>
                  {isSaving ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Menyimpan...</> : <><i className="bi bi-save me-1" />{isEditing ? 'Simpan Perubahan' : 'Simpan Obat Baru'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DETAIL */}
      {showModalDetail && selectedObat && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={e => { if (e.target === e.currentTarget) setShowModalDetail(false); }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 500, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 30px 70px rgba(0,0,0,0.35)', animation: 'modalPop 0.22s cubic-bezier(.34,1.56,.64,1)' }}>
            <div style={{ background: `linear-gradient(135deg,${C.navy},${C.navy2})`, padding: '16px 22px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                <i className="bi bi-info-circle-fill" style={{ color: '#38bdf8' }} /> Detail Spesifikasi Obat
              </div>
              <button onClick={() => setShowModalDetail(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 7, color: '#94a3b8', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1.1rem' }}>x</button>
            </div>
            <div style={{ padding: '20px 22px' }}>
              <div style={{ background: `linear-gradient(135deg,${C.navy},#1e3a5f)`, borderRadius: 12, padding: '18px 20px', marginBottom: 16, color: '#fff' }}>
                <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: '0.8px', marginBottom: 4 }}>NAMA OBAT</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 10 }}>{selectedObat.nm_obat}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Pill bg="rgba(56,189,248,0.2)" color="#38bdf8">{selectedObat.satuan||'-'}</Pill>
                  {(selectedObat.m1||'').toUpperCase()==='YA' ? <Pill bg="rgba(52,211,153,0.25)" color="#34d399">Formularium</Pill> : <Pill bg="rgba(148,163,184,0.2)" color="#94a3b8">Non-Formularium</Pill>}
                  {selectedObat.generik && selectedObat.generik.toUpperCase()!=='TIDAK' && <Pill bg="rgba(52,211,153,0.2)" color="#34d399">Generik</Pill>}
                </div>
              </div>
              {[
                ['Kode Obat 1', <span style={{ fontFamily: 'monospace', color: C.skyD, fontWeight: 700 }}>{selectedObat.kd_obat1}</span>],
                ['Kode Obat 2', <span style={{ fontFamily: 'monospace', color: C.muted }}>{selectedObat.kd_obat2||'-'}</span>],
                ['Harga Satuan', <span style={{ color: C.greenD, fontWeight: 700 }}>{fmt(selectedObat.harga)}</span>],
                ['Prinsipal', selectedObat.prinsipal||'-'],
                ['Zat Generik', selectedObat.generik||'-'],
                ['Stok Apotek', <Pill bg="rgba(2,132,199,0.1)" color={C.skyD}>{selectedObat.stoka||0} unit</Pill>],
                ['Stok Farmasi', <Pill bg="rgba(16,185,129,0.1)" color={C.greenD}>{selectedObat.stokf||0} unit</Pill>],
                ['Klasifikasi', selectedObat.khusus==='Y' ? <Pill bg="rgba(239,68,68,0.1)" color={C.red}>Khusus</Pill> : <span style={{ color: C.muted }}>Reguler</span>],
                ['Status Jamsostek', selectedObat.jamsostek||'-'],
              ].map(([label, value], i, arr) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < arr.length-1 ? `1px solid ${C.slate}` : 'none', fontSize: '0.82rem' }}>
                  <span style={{ color: C.light, fontWeight: 600, fontSize: '0.75rem' }}>{label}</span>
                  <span style={{ color: C.text, fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '13px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', background: C.slate, borderRadius: '0 0 16px 16px' }}>
              <button onClick={() => { setShowModalDetail(false); handleOpenEdit(selectedObat); }} style={{ background: '#fff', border: '1px solid #c7d2fe', borderRadius: 9, color: C.violet, padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><i className="bi bi-pencil" /> Edit Data</button>
              <button onClick={() => setShowModalDetail(false)} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, color: C.muted, padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>Tutup</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL HAPUS */}
      {confirmDel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', animation: 'modalPop 0.2s ease', overflow: 'hidden' }}>
            <div style={{ background: '#fef2f2', padding: '20px 22px', borderBottom: '1px solid #fecaca', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="bi bi-trash3-fill" style={{ color: C.red, fontSize: '1.1rem' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.95rem', marginBottom: 4 }}>Konfirmasi Hapus</div>
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d' }}>Anda akan menghapus obat:<br /><strong>{confirmDel.nm_obat}</strong> <span style={{ color: C.red }}>({confirmDel.kd_obat1})</span></div>
              </div>
            </div>
            <div style={{ padding: '14px 22px', background: C.slate, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setConfirmDel(null)} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 9, color: C.muted, padding: '8px 18px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>Batal</button>
              <button onClick={() => handleDelete(confirmDel)} style={{ background: `linear-gradient(135deg,${C.red},#dc2626)`, border: 'none', borderRadius: 9, color: '#fff', padding: '8px 20px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, boxShadow: '0 3px 10px rgba(239,68,68,0.3)' }}>
                <i className="bi bi-trash3" /> Ya, Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalPop { from { opacity:0; transform:scale(0.92) translateY(-12px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes spin { 100% { transform:rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>

      {/* Modal Isi Generik */}
      <Modal show={showIsiGenerikModal} onHide={() => setShowIsiGenerikModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 'bold' }}>
            <i className="bi bi-capsule-pill text-success me-2"></i> 
            Kandungan Generik: {selectedObatUntukGenerik?.nm_obat}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: '400px' }}>
            <Table striped bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: '0.8rem' }}>
              <thead className="table-success sticky-top">
                <tr>
                  <th className="text-center" style={{ width: '50px' }}>No</th>
                  <th>Kode Generik</th>
                  <th>Nama Generik</th>
                  <th className="text-center">Jumlah</th>
                  <th className="text-center">Satuan</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingIsiGenerik ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      <Spinner animation="border" variant="success" size="sm" />
                      <div className="mt-2 text-muted">Memuat data kandungan...</div>
                    </td>
                  </tr>
                ) : isiGenerikList.length > 0 ? (
                  isiGenerikList.map((isi, idx) => (
                    <tr key={idx}>
                      <td className="text-center text-muted fw-bold">{idx + 1}</td>
                      <td className="font-monospace fw-bold">{isi.kd_generik}</td>
                      <td className="fw-bold">{isi.nm_generik}</td>
                      <td className="text-center fw-bold text-success">{isi.jumlah}</td>
                      <td className="text-center">{isi.satuan}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      <i className="bi bi-info-circle fs-3 d-block mb-2"></i>
                      Tidak ada data kandungan generik untuk obat ini.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowIsiGenerikModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Persamaan Obat */}
      <Modal show={showPersamaanModal} onHide={() => setShowPersamaanModal(false)} centered size="xl">
        <Modal.Header closeButton className="bg-light">
          <div className="d-flex align-items-center justify-content-between w-100 me-2 flex-wrap gap-2">
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <i className="bi bi-shuffle text-teal me-2 fs-5"></i> 
              Persamaan Obat: <span className="text-primary ms-1">{selectedObatUntukPersamaan?.nm_obat}</span>
            </Modal.Title>
            {kdPersamaan && (
              <span className="badge bg-secondary font-monospace" style={{ fontSize: '0.75rem' }}>
                Kode Persamaan: {kdPersamaan}
              </span>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: '450px' }}>
            <Table striped bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: '0.78rem' }}>
              <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                <tr className="text-secondary">
                  <th className="text-center" style={{ width: '45px' }}>No</th>
                  <th style={{ width: '90px' }}>Kode</th>
                  <th>Nama Obat</th>
                  <th>Pabrik / Produsen</th>
                  <th className="text-center" style={{ width: '70px' }}>Satuan</th>
                  <th className="text-center" style={{ width: '70px' }}>Stok</th>
                  <th className="text-end" style={{ width: '100px' }}>HDA Beli</th>
                  <th className="text-end" style={{ width: '100px' }}>HDA Jual</th>
                  <th className="text-end" style={{ width: '100px' }}>Pricelist</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingPersamaan ? (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      <Spinner animation="border" variant="teal" size="sm" className="mb-2" />
                      <div>Memuat data persamaan obat...</div>
                    </td>
                  </tr>
                ) : persamaanList.length > 0 ? (
                  persamaanList.map((item, idx) => {
                    const isCurrent = item.kdobat === (selectedObatUntukPersamaan?.kd_obat1 || selectedObatUntukPersamaan?.kdbr);
                    return (
                      <tr key={idx} style={{ backgroundColor: isCurrent ? 'rgba(13, 148, 136, 0.08)' : undefined }}>
                        <td className="text-center text-muted fw-bold">{idx + 1}</td>
                        <td className="font-monospace fw-bold text-teal">{item.kdobat}</td>
                        <td className="fw-bold text-dark">
                          {item.nmobat}
                          {isCurrent && (
                            <span className="badge bg-teal ms-2 text-white" style={{ fontSize: '0.65rem' }}>Item Ini</span>
                          )}
                        </td>
                        <td className="text-muted">{item.pabrik || '-'}</td>
                        <td className="text-center">{item.satuan || '-'}</td>
                        <td className="text-center">
                          <span className={`badge ${parseInt(item.stok || '0', 10) > 0 ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.72rem' }}>
                            {item.stok}
                          </span>
                        </td>
                        <td className="text-end font-monospace">{item.hdabeli ? `Rp ${item.hdabeli}` : '-'}</td>
                        <td className="text-end font-monospace text-success fw-bold">{item.hdajual ? `Rp ${item.hdajual}` : '-'}</td>
                        <td className="text-end font-monospace text-muted">{item.pricelist ? `Rp ${item.pricelist}` : '-'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      <i className="bi bi-info-circle fs-3 d-block mb-2 opacity-50"></i>
                      Tidak ada data persamaan untuk obat ini.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light py-2 d-flex justify-content-between align-items-center">
          <small className="text-muted">
            Total persamaan: <strong>{persamaanList.length}</strong> item
          </small>
          <div className="d-flex gap-2">
            <Button variant="teal" size="sm" className="d-flex align-items-center gap-1.5 fw-bold text-white" style={{ background: '#0d9488', border: 'none' }} onClick={() => setShowModalLaporan(true)}>
              <i className="bi bi-printer-fill"></i> Laporan Persamaan
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setShowPersamaanModal(false)}>
              Tutup
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      

      {/* DIALOG WINDOWS: STOK & PERSAMAAN OBAT (TableSama Gambas Replica) */}
      <Modal 
        show={showStokModal} 
        onHide={() => setShowStokModal(false)} 
        size="xl" 
        centered 
        backdrop="static"
      >
        <div style={{
          backgroundColor: '#4a4a4a',
          border: '2px solid #222',
          borderRadius: '3px',
          overflow: 'hidden',
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
        }}>
          {/* Top Window Bar (Dark Header with Window Controls) */}
          <div style={{
            background: 'linear-gradient(to bottom, #333333, #1f1f1f)',
            padding: '4px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #111'
          }}>
            <div style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="bi bi-box-seam" style={{ color: '#38bdf8' }}></i>
              STANDAR OBAT DAN PERSEDIAAN (TableSama)
            </div>
            <button 
              type="button" 
              onClick={() => setShowStokModal(false)}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '3px',
                width: '22px',
                height: '20px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Tutup"
            >
              ✕
            </button>
          </div>

          {/* Window Body */}
          <div style={{ padding: '8px' }}>
            {/* Top Row: NAMA OBAT | SATUAN (Blue Box) + Kode/Nama Display Box + Search Box */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
              {/* Blue Box */}
              <div style={{
                backgroundColor: '#003399',
                color: '#ffffff',
                padding: '4px 10px',
                fontWeight: 'bold',
                fontSize: '0.78rem',
                letterSpacing: '0.5px',
                borderRadius: '2px',
                border: '1px solid #002277',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center'
              }}>
                NAMA OBAT | SATUAN
              </div>

              {/* Display Box for Obat Aktif */}
              <div style={{ flex: 1 }}>
                <input 
                  type="text" 
                  readOnly 
                  value={selectedObatStok ? `${selectedObatStok.kd_obat1 || selectedObatStok.kdbr || selectedObatStok.kode || ''} - ${selectedObatStok.nm_obat || selectedObatStok.nama || ''} | ${selectedObatStok.satuan || '-'}` : ''}
                  style={{
                    width: '100%',
                    padding: '3px 8px',
                    fontSize: '0.78rem',
                    fontWeight: 'bold',
                    backgroundColor: '#ffffff',
                    border: '1px solid #333',
                    borderRadius: '2px',
                    color: '#000',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Right Box: txtkdpersamaan / filter */}
              <div style={{ width: '180px' }}>
                <input 
                  type="text" 
                  placeholder="Cari dalam tabel..."
                  value={filterTableSama}
                  onChange={(e) => setFilterTableSama(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '3px 8px',
                    fontSize: '0.78rem',
                    backgroundColor: '#ffffff',
                    border: '1px solid #333',
                    borderRadius: '2px',
                    color: '#000',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Main Table: TableSama */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid #222', minHeight: '340px', maxHeight: '55vh', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  {/* Header Row 1 */}
                  <tr>
                    {/* Red [...] Button in top-left */}
                    <th 
                      rowSpan={2}
                      style={{
                        backgroundColor: '#880000',
                        color: '#fff',
                        width: '38px',
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        border: '1px solid #111',
                        padding: '2px',
                        cursor: 'pointer'
                      }}
                      onClick={handleCekStokRealtime}
                      title="Klik untuk Hitung & Perbarui Stok Real-time (cekstok)"
                    >
                      {isCheckingStok ? (
                        <span className="spinner-border spinner-border-sm text-light" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                      ) : (
                        <span style={{ fontSize: '0.95rem', fontWeight: 'bold' }}>...</span>
                      )}
                    </th>
                    <th 
                      rowSpan={2}
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '5px 8px',
                        fontWeight: 'bold',
                        fontSize: '0.78rem',
                        letterSpacing: '0.5px',
                        border: '1px solid #004d4d',
                        textAlign: 'left'
                      }}
                    >
                      PERSEDIAN OBAT DAN ALKES
                    </th>
                    <th 
                      rowSpan={2}
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '5px 8px',
                        fontWeight: 'bold',
                        fontSize: '0.78rem',
                        letterSpacing: '0.5px',
                        border: '1px solid #004d4d',
                        textAlign: 'center',
                        width: '75px'
                      }}
                    >
                      SATUAN
                    </th>
                    <th 
                      rowSpan={2}
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '5px 8px',
                        fontWeight: 'bold',
                        fontSize: '0.78rem',
                        letterSpacing: '0.5px',
                        border: '1px solid #004d4d',
                        textAlign: 'center',
                        width: '55px'
                      }}
                    >
                      STDR
                    </th>
                    <th 
                      rowSpan={2}
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '5px 8px',
                        fontWeight: 'bold',
                        fontSize: '0.78rem',
                        letterSpacing: '0.5px',
                        border: '1px solid #004d4d',
                        textAlign: 'right',
                        width: '100px'
                      }}
                    >
                      HARGA NETT
                    </th>
                    {/* Grouped Header: STOK */}
                    <th 
                      colSpan={2}
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '2px 6px',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        letterSpacing: '0.5px',
                        border: '1px solid #004d4d',
                        textAlign: 'center'
                      }}
                    >
                      STOK
                    </th>
                    {/* Top Right: BERSIH Button */}
                    <th 
                      rowSpan={2}
                      style={{
                        backgroundColor: '#4682b4',
                        color: '#ffffff',
                        padding: '2px 8px',
                        fontWeight: 'bold',
                        fontSize: '0.72rem',
                        letterSpacing: '0.5px',
                        border: '1px solid #36648b',
                        textAlign: 'center',
                        width: '60px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setFilterTableSama('')}
                      title="Reset Filter"
                    >
                      BERSIH
                    </th>
                  </tr>
                  {/* Header Row 2: Sub-headers for STOK (RJ & RI) */}
                  <tr>
                    <th 
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '2px 6px',
                        fontWeight: 'bold',
                        fontSize: '0.72rem',
                        border: '1px solid #004d4d',
                        textAlign: 'center',
                        width: '55px'
                      }}
                    >
                      RJ
                    </th>
                    <th 
                      style={{
                        backgroundColor: '#006666',
                        color: '#ffffff',
                        padding: '2px 6px',
                        fontWeight: 'bold',
                        fontSize: '0.72rem',
                        border: '1px solid #004d4d',
                        textAlign: 'center',
                        width: '55px'
                      }}
                    >
                      RI
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingTableSama ? (
                    <tr>
                      <td colSpan={8} style={{ padding: '50px 20px', textAlign: 'center', color: '#64748b' }}>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Memuat data TableSama...
                      </td>
                    </tr>
                  ) : filteredTableSama.length === 0 ? (
                    <tr>
                      <td style={{ border: '1px solid #cbd5e1', padding: '6px 8px', color: '#0022cc', fontWeight: 'bold' }}>
                        TableSama
                      </td>
                      <td colSpan={7} style={{ border: '1px solid #cbd5e1', padding: '6px 8px', color: '#94a3b8' }}>
                        Tidak ada data persamaan atau stok obat.
                      </td>
                    </tr>
                  ) : (
                    filteredTableSama.map((item, idx) => {
                      const isSelected = selectedObatStok && (selectedObatStok.kd_obat1 === item.kd_obat || selectedObatStok.kdbr === item.kd_obat || selectedObatStok.kode === item.kd_obat);
                      return (
                        <tr 
                          key={idx} 
                          style={{
                            backgroundColor: isSelected ? '#ecfdf5' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                            borderBottom: '1px solid #cbd5e1',
                            transition: 'background 0.12s'
                          }}
                        >
                          {/* Col 0: TableSama / Index */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 6px', textAlign: 'center', color: isSelected ? '#059669' : '#0022cc', fontWeight: 'bold', fontSize: '0.72rem' }}>
                            {idx === 0 && !filterTableSama ? 'TableSama' : (idx + 1)}
                          </td>
                          {/* Col 1: PERSEDIAN OBAT DAN ALKES */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#003399', marginRight: '8px' }}>
                              {item.kd_obat}
                            </span>
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                              {item.nm_obat}
                            </span>
                            {isSelected && (
                              <span style={{ marginLeft: '6px', fontSize: '0.65rem', backgroundColor: '#d1fae5', color: '#047857', padding: '1px 5px', borderRadius: '3px', fontWeight: 'bold' }}>
                                Item Ini
                              </span>
                            )}
                          </td>
                          {/* Col 2: SATUAN */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 6px', textAlign: 'center', color: '#475569' }}>
                            {item.satuan}
                          </td>
                          {/* Col 3: STDR (M) */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 6px', textAlign: 'center' }}>
                            {item.m === 'V' || item.m === 'Ya' ? (
                              <span style={{ color: '#059669', fontWeight: 'bold' }}>V</span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>X</span>
                            )}
                          </td>
                          {/* Col 4: HARGA NETT */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', color: '#0f766e' }}>
                            {item.harga ? Number(item.harga).toLocaleString('id-ID') : '0'}
                          </td>
                          {/* Col 5: STOK RJ */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: item.stoka > 0 ? 'bold' : 'normal', color: item.stoka > 0 ? '#15803d' : '#64748b' }}>
                            {item.stoka}
                          </td>
                          {/* Col 6: STOK RI */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: item.stokf > 0 ? 'bold' : 'normal', color: item.stokf > 0 ? '#0284c7' : '#64748b' }}>
                            {item.stokf}
                          </td>
                          {/* Col 7: Action / Select */}
                          <td style={{ border: '1px solid #cbd5e1', padding: '2px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => {
                                handleOpenStok({ kd_obat1: item.kd_obat, nm_obat: item.nm_obat, satuan: item.satuan });
                              }}
                              title="Pilih obat ini"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#0284c7',
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                padding: 0
                              }}
                            >
                              <i className="bi bi-arrow-right-circle" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom Status Bar */}
            <div style={{
              marginTop: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#ffffff',
              fontSize: '0.74rem'
            }}>
              <div>
                <span>KD Persamaan: <strong style={{ color: '#38bdf8' }}>{kdPersamaanStok || '-'}</strong></span>
                <span style={{ margin: '0 8px' }}>|</span>
                <span>Total Padanan Obat: <strong>{tableSamaList.length}</strong> item</span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  onClick={handleCekStokRealtime}
                  disabled={isCheckingStok}
                  style={{
                    background: '#059669',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '2px',
                    padding: '3px 10px',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {isCheckingStok ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-arrow-repeat" />}
                  Hitung Ulang Stok
                </button>
                <button
                  type="button"
                  onClick={() => setShowStokModal(false)}
                  style={{
                    background: '#64748b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '2px',
                    padding: '3px 12px',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                >
                  Tutup
                </button>
              </div>
            </div>

          </div>
        </div>
      </Modal>

      {/* MODAL DIALOG WINDOW: KARTU STOK (Pergerakan Barang) */}
      <Modal 
        show={showModalKartuStok} 
        onHide={() => setShowModalKartuStok(false)} 
        centered 
        size="md"
        backdrop="static"
      >
        <Modal.Header 
          closeButton 
          style={{ background: '#0f172a', color: '#fff', borderBottom: '2px solid #0284c7' }}
          className="py-2.5 px-3.5"
        >
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-card-checklist text-info"></i>
            <span>Kartu Stok (Pergerakan Barang)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3" style={{ background: '#f8fafc' }}>
          {/* Obat Info Box */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Informasi Obat</div>
            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
              <span style={{ color: '#0284c7', marginRight: 6 }}>
                [{selectedObatKartuStok?.kdbr || selectedObatKartuStok?.kd_obat1 || selectedObatKartuStok?.kd_obat || '-'}]
              </span>
              {selectedObatKartuStok?.nama || selectedObatKartuStok?.nm_obat || '-'}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: '0.75rem', color: '#475569' }}>
              <span>Satuan: <strong>{selectedObatKartuStok?.kdsatuan2 || selectedObatKartuStok?.satuan || '-'}</strong></span>
              <span>Pabrik: <strong>{selectedObatKartuStok?.pabrik || selectedObatKartuStok?.prinsipal || '-'}</strong></span>
            </div>
          </div>

          {/* Pilihan Lokasi */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
              Pilih Lokasi Gudang:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 8,
                border: `2px solid ${lokasiKartuStok === '00001' ? '#0284c7' : '#e2e8f0'}`,
                background: lokasiKartuStok === '00001' ? '#f0f9ff' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}>
                <input 
                  type="radio" 
                  name="lokasiKartuStok" 
                  value="00001" 
                  checked={lokasiKartuStok === '00001'} 
                  onChange={() => setLokasiKartuStok('00001')} 
                  style={{ accentColor: '#0284c7', width: 16, height: 16 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>Apotek (00001)</div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Gudang Pelayanan Apotek</div>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 8,
                border: `2px solid ${lokasiKartuStok === '00002' ? '#059669' : '#e2e8f0'}`,
                background: lokasiKartuStok === '00002' ? '#ecfdf5' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}>
                <input 
                  type="radio" 
                  name="lokasiKartuStok" 
                  value="00002" 
                  checked={lokasiKartuStok === '00002'} 
                  onChange={() => setLokasiKartuStok('00002')} 
                  style={{ accentColor: '#059669', width: 16, height: 16 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>Farmasi (00002)</div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Gudang Logistik Farmasi</div>
                </div>
              </label>
            </div>
          </div>

          {/* Pilihan Periode */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                Periode Transaksi:
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    setTglAwalKartuStok(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
                    setTglAkhirKartuStok(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                  }}
                  className="btn btn-xs btn-outline-secondary"
                  style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4 }}
                >
                  Bulan Ini
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setMonth(d.getMonth() - 1);
                    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
                    setTglAwalKartuStok(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
                    setTglAkhirKartuStok(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`);
                  }}
                  className="btn btn-xs btn-outline-secondary"
                  style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4 }}
                >
                  Bulan Lalu
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                  Periode 1 (Mulai):
                </label>
                <input 
                  type="date" 
                  value={tglAwalKartuStok} 
                  onChange={(e) => setTglAwalKartuStok(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ fontSize: '0.8rem', fontWeight: 600 }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>
                  Periode 2 (Sampai):
                </label>
                <input 
                  type="date" 
                  value={tglAkhirKartuStok} 
                  onChange={(e) => setTglAkhirKartuStok(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ fontSize: '0.8rem', fontWeight: 600 }}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between align-items-center">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={() => setShowModalKartuStok(false)}
            style={{ fontSize: '0.82rem', padding: '4px 12px' }}
          >
            Batal
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleProsesKartuStok}
            className="d-flex align-items-center gap-1.5 fw-bold"
            style={{ fontSize: '0.82rem', padding: '4px 16px', background: '#0284c7', borderColor: '#0284c7' }}
          >
            <i className="bi bi-file-earmark-pdf-fill"></i>
            Proses Kartu Stok
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DIALOG WINDOWS: STANDAR OBAT DAN AKSP (Gambas Replica) */}
      <Modal show={showModalLaporan} onHide={() => setShowModalLaporan(false)} centered size="md" backdrop="static">
        <div style={{ border: '2px solid #0022cc', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#f0f0f0', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
          
          {/* Top Title Bar (Blue) */}
          <div style={{ backgroundColor: '#0022cc', color: '#ffffff', padding: '7px 12px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', fontSize: '1.05rem', letterSpacing: '0.8px', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            STANDAR OBAT DAN AKSP
          </div>

          <div style={{ padding: '8px' }}>
            
            {/* Box 1: Disusun Berdasarkan */}
            <div style={{ border: '1px solid #0022cc', borderRadius: '3px', marginBottom: '8px', overflow: 'hidden', backgroundColor: '#fff' }}>
              <div style={{ backgroundColor: '#0022cc', color: '#fff', padding: '3px 8px', fontSize: '0.78rem', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                Disusun Berdasarkan:
              </div>
              <div style={{ padding: '6px 8px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                  <select 
                    value={cmbDisusun} 
                    onChange={(e) => setCmbDisusun(Number(e.target.value))}
                    disabled={optTidakStandar}
                    style={{ flex: 1, padding: '3px 6px', fontSize: '0.78rem', borderRadius: '2px', border: '1px solid #999', backgroundColor: optTidakStandar ? '#e9ecef' : '#fff', fontWeight: '500' }}
                  >
                    <option value={1}>1. Prinsipal</option>
                    <option value={2}>2. Kelas Trapi &amp; Golongan</option>
                    <option value={3}>3. Standar Rumah Sakit dan Klasifikasi</option>
                    <option value={4}>4. Standar Khusus</option>
                    <option value={5}>5. Prinsipal + seleksi persamaan prinsipal</option>
                    <option value={6}>6. Standar BPJS</option>
                    <option value={7}>7. Prinsipal dengan stok</option>
                  </select>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#0022cc', color: '#fff', padding: '2px 8px', borderRadius: '2px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    <span>Margin</span>
                    <input 
                      type="number" 
                      step="0.01" 
                      value={txtMargin} 
                      onChange={(e) => setTxtMargin(e.target.value)} 
                      style={{ width: '55px', padding: '1px 4px', fontSize: '0.78rem', textAlign: 'center', borderRadius: '2px', border: '1px solid #fff', color: '#000', fontWeight: 'bold' }} 
                    />
                  </div>
                </div>

                {/* Input Kode & Nama */}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <div style={{ display: 'flex', width: '130px' }}>
                    <input 
                      type="text" 
                      placeholder={Number(cmbDisusun) === 3 ? "Kode Klas" : "Kode Prinsip"}
                      value={txtKode} 
                      onChange={(e) => setTxtKode(e.target.value)}
                      style={{ width: '100%', padding: '3px 6px', fontSize: '0.78rem', borderRadius: '2px 0 0 2px', border: '1px solid #999', fontFamily: 'monospace', fontWeight: 'bold' }}
                    />
                    <button 
                      type="button" 
                      onClick={() => openLookup(Number(cmbDisusun) === 3 ? 'klasifikasi' : 'prinsipal', 'field1')}
                      title="Cari"
                      style={{ background: '#e2e8f0', border: '1px solid #999', borderLeft: 'none', borderRadius: '0 2px 2px 0', padding: '0 6px', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      <i className="bi bi-search" />
                    </button>
                  </div>
                  <input 
                    type="text" 
                    placeholder={Number(cmbDisusun) === 3 ? "Nama Klasifikasi..." : "Nama Prinsipal..."}
                    value={txtNama} 
                    onChange={(e) => setTxtNama(e.target.value)}
                    style={{ flex: 1, padding: '3px 6px', fontSize: '0.78rem', borderRadius: '2px', border: '1px solid #999', backgroundColor: '#f8fafc' }}
                  />
                  {(txtKode || txtNama) && (
                    <button 
                      type="button" 
                      onClick={() => { setTxtKode(''); setTxtNama(''); }}
                      style={{ background: '#fee2e2', border: '1px solid #f87171', color: '#b91c1c', borderRadius: '2px', padding: '0 6px', cursor: 'pointer' }}
                    >
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Box 2: Sisa Persediaan Tidak Standar Akan Dihabiskan (Red Banner) */}
            <div 
              style={{ backgroundColor: '#cc0000', color: '#ffffff', padding: '5px 10px', borderRadius: '3px', marginBottom: '8px', display: 'flex', alignItems: 'center', cursor: 'pointer', border: '1px solid #990000' }}
              onClick={() => setOptTidakStandar(!optTidakStandar)}
            >
              <input 
                type="radio" 
                checked={optTidakStandar} 
                onChange={(e) => setOptTidakStandar(e.target.checked)} 
                style={{ cursor: 'pointer', marginRight: '8px', transform: 'scale(1.2)' }}
              />
              <span style={{ fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '0.3px', userSelect: 'none' }}>
                Sisa Persediaan Tidak Standar Akan Dihabiskan
              </span>
            </div>

            {/* Box 4: Action Bar (Progress, Proses, Tutup) */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: Number(cmbDisusun) === 5 ? '8px' : '0', alignItems: 'stretch' }}>
              {/* Progress Indicator */}
              <div style={{ width: '110px', display: 'flex', alignItems: 'center' }}>
                <ProgressBar 
                  now={progValue} 
                  label={`${progValue}%`} 
                  animated={isProcessing} 
                  striped={isProcessing}
                  variant={progValue === 100 ? "success" : "primary"}
                  style={{ width: '100%', height: '34px', fontSize: '0.82rem', fontWeight: 'bold', borderRadius: '3px', border: '1px solid #2b6cb0', backgroundColor: '#e2e8f0' }} 
                />
              </div>

              {/* Tombol Proses */}
              <button 
                type="button" 
                onClick={handleProsesLaporan} 
                disabled={isProcessing}
                style={{ flex: 1, background: 'linear-gradient(to bottom, #64748b, #475569)', color: '#fff', border: '1px solid #334155', borderRadius: '3px', padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.86rem', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
              >
                {isProcessing ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <i className="bi bi-printer-fill fs-5" style={{ color: '#67e8f9' }} />
                )}
                <span><u>P</u>roses</span>
              </button>

              {/* Tombol Tutup */}
              <button 
                type="button" 
                onClick={() => setShowModalLaporan(false)}
                style={{ width: '100px', background: 'linear-gradient(to bottom, #64748b, #475569)', color: '#fff', border: '1px solid #334155', borderRadius: '3px', padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.86rem', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
              >
                <i className="bi bi-power fs-5" style={{ color: '#ef4444' }} />
                <span><u>T</u>utup</span>
              </button>
            </div>

            {/* Box 5: Panel TabelPeriksa (Tampil jika Opsi 5 Dipilih) */}
            {Number(cmbDisusun) === 5 && (
              <div style={{ border: '1px solid #0022cc', borderRadius: '3px', overflow: 'hidden', backgroundColor: '#fff', marginTop: '8px' }}>
                <div style={{ backgroundColor: '#0022cc', color: '#fff', padding: '3px 8px', fontSize: '0.76rem', fontWeight: 'bold' }}>
                  Seleksi Persamaan Prinsipal (TabelPeriksa)
                </div>
                <div style={{ padding: '6px 8px' }}>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                    <div style={{ display: 'flex', width: '130px' }}>
                      <input 
                        type="text" 
                        placeholder="Kode Prinsip" 
                        value={txtKode2} 
                        onChange={(e) => setTxtKode2(e.target.value)} 
                        style={{ width: '100%', padding: '2px 5px', fontSize: '0.76rem', border: '1px solid #999', borderRadius: '2px 0 0 2px', fontFamily: 'monospace' }}
                      />
                      <button 
                        type="button" 
                        onClick={() => openLookup('prinsipal', 'field2')} 
                        style={{ background: '#e2e8f0', border: '1px solid #999', borderLeft: 'none', borderRadius: '0 2px 2px 0', padding: '0 6px', cursor: 'pointer' }}
                      >
                        <i className="bi bi-search" style={{ fontSize: '0.7rem' }} />
                      </button>
                    </div>
                    <input 
                      type="text" 
                      placeholder="Nama Prinsipal..." 
                      value={txtNama2} 
                      onChange={(e) => setTxtNama2(e.target.value)} 
                      style={{ flex: 1, padding: '2px 5px', fontSize: '0.76rem', border: '1px solid #999', borderRadius: '2px' }}
                    />
                    <button 
                      type="button" 
                      onClick={handleAddSeleksiPrinsipal}
                      style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: '2px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      + Tambah
                    </button>
                  </div>

                  {/* TabelPeriksa */}
                  <div style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid #cbd5e1' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                      <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ border: '1px solid #cbd5e1', padding: '3px 6px', width: '90px' }}>Kode</th>
                          <th style={{ border: '1px solid #cbd5e1', padding: '3px 6px' }}>Nama Prinsipal</th>
                          <th style={{ border: '1px solid #cbd5e1', padding: '3px 6px', width: '45px', textAlign: 'center' }}>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tabelPeriksa.length > 0 ? (
                          tabelPeriksa.map((p, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                              <td style={{ border: '1px solid #cbd5e1', padding: '3px 6px', fontFamily: 'monospace', fontWeight: 'bold', color: '#0022cc' }}>{p.kd_prinsipal}</td>
                              <td style={{ border: '1px solid #cbd5e1', padding: '3px 6px' }}>{p.nm_prinsipal}</td>
                              <td style={{ border: '1px solid #cbd5e1', padding: '2px', textAlign: 'center' }}>
                                <button 
                                  type="button" 
                                  onClick={() => handleRemoveSeleksiPrinsipal(p.kd_prinsipal)} 
                                  style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '2px', padding: '1px 5px', fontSize: '0.65rem', cursor: 'pointer' }}
                                >
                                  <i className="bi bi-trash" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" style={{ textAlign: 'center', padding: '12px', color: '#94a3b8' }}>
                              Belum ada prinsipal yang dipilih dalam TabelPeriksa.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </Modal>

      {/* MODAL PRATINJAU PDF LAPORAN PERSAMAAN */}
      <Modal 
        show={showModalPdf} 
        onHide={() => { 
          setShowModalPdf(false); 
          if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl('');
          }
        }} 
        size="xl" 
        centered 
        backdrop="static"
      >
        <Modal.Header 
          closeButton={!isProcessing} 
          style={{ background: '#0f172a', color: '#fff', borderBottom: '2px solid #0d9488' }} 
          className="py-2 px-3 d-flex justify-content-between align-items-center"
        >
          <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 600 }}>
            <i className="bi bi-file-earmark-pdf-fill text-danger me-2"></i>
            {pdfTitle}
          </Modal.Title>
          {pdfUrl && (
            <div className="d-flex align-items-center gap-2 me-3">
              <a 
                href={pdfUrl} 
                download={pdfFilename} 
                className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                style={{ fontSize: '0.78rem', padding: '3px 8px' }}
              >
                <i className="bi bi-download"></i> Unduh PDF
              </a>
            </div>
          )}
        </Modal.Header>
        <Modal.Body className="p-0 bg-light position-relative" style={{ height: '80vh' }}>
          {pdfUrl ? (
            <iframe 
              src={pdfUrl} 
              title={pdfTitle} 
              width="100%" 
              height="100%" 
              style={{ border: 'none' }}
            />
          ) : (
            <div className="d-flex flex-column h-100 justify-content-center align-items-center bg-white p-4">
              <div className="text-center mb-4">
                <i className="bi bi-file-earmark-pdf text-teal" style={{ fontSize: '4.5rem', color: '#0d9488', opacity: 0.9 }}></i>
                <h5 className="mt-3 fw-bold text-dark">Membuat Laporan PDF...</h5>
                <p className="text-muted small">Sedang memproses data dan merender dokumen PDF dari server.</p>
              </div>
              <div style={{ width: '60%', maxWidth: '500px' }}>
                <ProgressBar 
                  animated 
                  now={pdfProgress} 
                  label={`${pdfProgress}%`} 
                  variant="primary" 
                  style={{ height: '24px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 'bold' }} 
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 px-3 bg-light d-flex justify-content-between align-items-center">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Gunakan kontrol PDF pada viewer untuk memperbesar, memutar, atau mencetak dokumen.
          </small>
          <Button 
            variant="secondary" 
            size="sm" 
            disabled={isProcessing}
            onClick={() => { 
              setShowModalPdf(false); 
              if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
                setPdfUrl('');
              }
            }}
          >
            Tutup Pratinjau
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL LOOKUP PRINSIPAL / KLASIFIKASI */}
      <Modal show={showLookupModal} onHide={() => setShowLookupModal(false)} centered size="sm">
        <Modal.Header closeButton className="py-2 bg-light">
          <Modal.Title style={{ fontSize: '0.88rem', fontWeight: 'bold' }}>
            Pilih {lookupType === 'prinsipal' ? 'Prinsipal' : 'Klasifikasi'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <input 
            type="text" 
            placeholder="Ketik pencarian..." 
            value={lookupSearch} 
            onChange={(e) => setLookupSearch(e.target.value)} 
            className="form-control form-control-sm mb-2"
            style={{ fontSize: '0.78rem' }}
          />
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {isLoadingLookup ? (
              <div className="text-center py-3 text-muted"><Spinner size="sm" animation="border" /> Memuat data...</div>
            ) : (
              <table className="table table-hover table-sm mb-0" style={{ fontSize: '0.75rem' }}>
                <tbody>
                  {lookupList
                    .filter(item => {
                      const txt = lookupType === 'prinsipal' ? `${item.kode} ${item.nama}` : `${item.kd_klas} ${item.nm_klas}`;
                      return !lookupSearch || txt.toLowerCase().includes(lookupSearch.toLowerCase());
                    })
                    .slice(0, 50)
                    .map((item, idx) => (
                      <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => selectLookupItem(item)}>
                        <td className="font-monospace fw-bold text-primary" style={{ width: '80px' }}>
                          {lookupType === 'prinsipal' ? item.kode : item.kd_klas}
                        </td>
                        <td>{lookupType === 'prinsipal' ? item.nama : item.nm_klas}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal.Body>
      </Modal>




    </div>
  );

};

export default MasterObat;
